import { useState } from 'react';
import db from '@/db';
import { useAuth } from '@/context/AuthContext';
import { addTombstone } from '@/lib/tombstones';
import { logAudit } from '@/lib/audit';
import { formatCurrency, formatDateTime } from '@/lib/formatters';
import { previewDeliveryLabel, previewKitchenLabel } from '@/lib/printer';
import { sendWhatsAppWebhook } from '@/lib/notifications';
import { notifyOrderDelivering } from '@/lib/zapiWhatsApp';
import { markOrderDelivering, markOrderDelivered } from '@/lib/deliveryPin';
import DeliveryPinDialog from '@/components/DeliveryPinDialog';
import { Badge } from '@/components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { Bike, CheckCircle, ChefHat, Package, Eye, MapPin, Printer, Layers, Tag, Trash2, ShieldAlert, ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';
import { useVisiblePolling } from '@/hooks/useVisiblePolling';

const STATUS_MAP = {
  preparing: { label: 'Preparando', color: 'bg-amber-100 text-amber-700 border-amber-200', icon: ChefHat },
  ready: { label: 'Pronto', color: 'bg-blue-100 text-blue-700 border-blue-200', icon: Package },
  delivering: { label: 'A caminho', color: 'bg-purple-100 text-purple-700 border-purple-200', icon: Bike },
  delivered: { label: 'Entregue', color: 'bg-emerald-100 text-emerald-700 border-emerald-200', icon: CheckCircle },
};

export default function DeliveryAdminPage() {
  const { operator } = useAuth();
  const isAdmin = operator?.role === 'admin';
  const [orders, setOrders] = useState([]);
  const [filter, setFilter] = useState('active');
  const [view, setView] = useState('list');
  const [labelPreview, setLabelPreview] = useState(null);
  const [labelType, setLabelType] = useState('delivery');
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [pinTarget, setPinTarget] = useState(null); // pedido aguardando confirmação via PIN

  const loadOrders = async () => {
    // Fase 27 — limit + index reverso. Evita carregar todo histórico.
    const recent = await db.deliveryOrders.orderBy('createdAt').reverse().limit(200).toArray();
    setOrders(recent);
  };

  useVisiblePolling(loadOrders, 8000);

  const filtered = orders.filter((o) => {
    if (filter === 'active') return ['preparing', 'ready', 'delivering'].includes(o.status);
    if (filter === 'delivered') return o.status === 'delivered';
    return true;
  });

  const updateStatus = async (orderId, newStatus) => {
    // v7.33 — Blindagem: 'delivered' só via PIN (em outro fluxo), nunca por
    // botão direto. Esta função NÃO aceita newStatus='delivered'.
    if (newStatus === 'delivered') {
      const order = orders.find((o) => o.id === orderId);
      if (order) setPinTarget(order);
      else toast.error('Pedido não encontrado');
      return;
    }
    if (newStatus === 'delivering') {
      try {
        // v7.35 — PIN gerado mas NÃO exibido ao admin (só o cliente vê).
        await markOrderDelivering(orderId);
        const updated = await db.deliveryOrders.get(orderId);
        await sendWhatsAppWebhook(updated, 'delivering', { toast });
        notifyOrderDelivering(updated).catch((e) => console.warn('[Z-API]', e));
        toast.success(`Pedido #${orderId} — Saiu para Entrega`, {
          description: 'PIN enviado ao cliente. Peça-o ao entregar.',
          duration: 5000,
        });
      } catch (err) {
        toast.error(err?.message || 'Falha ao despachar');
      }
      loadOrders();
      return;
    }
    // Demais transições (preparing → ready / voltar pra cozinha)
    // v7.94 — CORREÇÕES CRÍTICAS:
    //  (1) updatedAt SEMPRE: sem ele o LWW do sync REJEITAVA a mudança nos
    //      outros aparelhos (pedido 'Pronto' aqui, invisível no entregador).
    //  (2) Regrediu pra preparing/ready → limpa a atribuição residual
    //      (assignedTo de rota revertida escondia o pedido de TODOS os
    //      entregadores no filtro da tela deles).
    const patch = { status: newStatus, updatedAt: new Date().toISOString() };
    if (newStatus === 'preparing' || newStatus === 'ready') {
      patch.assignedTo = null;
      patch.assignedName = null;
    }
    await db.deliveryOrders.update(orderId, patch);
    const updated = await db.deliveryOrders.get(orderId);
    if (updated) {
      // Push explícito (mesmo padrão do KDS v7.68): garante replicação
      try {
        const { pushToRemote } = await import('@/lib/firebaseSync');
        await pushToRemote('deliveryOrders', orderId, updated);
      } catch {}
      await sendWhatsAppWebhook(updated, newStatus, { toast });
    }
    toast.success(`Pedido #${orderId} -> ${STATUS_MAP[newStatus]?.label || newStatus}`);
    loadOrders();
  };

  const confirmDeliveryPin = async (pin) => {
    const order = pinTarget;
    if (!order) return;
    await markOrderDelivered(order.id, pin, { deliveredBy: operator?.name });
    await sendWhatsAppWebhook({ ...order, status: 'delivered' }, 'delivered', { toast });
    toast.success(`Pedido #${order.id} entregue (PIN validado)`);
    setPinTarget(null);
    loadOrders();
  };

  const showLabel = (order, type) => {
    setLabelType(type);
    setLabelPreview(type === 'kitchen' ? previewKitchenLabel(order) : previewDeliveryLabel(order));
  };

  // Fase 30 — Admin: excluir pedido de delivery (com tombstone p/ não voltar)
  // v7.82 — Exclusão DEFINITIVA em TODAS as camadas: Dexie + Firebase (nó +
  // tombstone via hook 'deleting') + card da cozinha (kdsQueue) + ponte
  // backendOrders + Mongo (servidor). Nada local-only.
  const handleConfirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      const order = deleteTarget;
      const totalBR = `R$ ${Number(order.total || 0).toFixed(2).replace('.', ',')}`;
      // 1) Tombstone — bloqueia ressurreição via Firebase sync
      await addTombstone('deliveryOrders', order.id, {
        customer: order.customerName,
        by: operator?.name || null,
      });
      // 2) Audit log
      await logAudit({
        table: 'deliveryOrders',
        action: 'delete',
        record: { id: order.id },
        modifications: null,
        overrideSummary: `${operator?.name || 'Admin'} excluiu pedido delivery #${order.id} (${order.status}) — ${totalBR} • ${order.customerName || 'sem nome'}`,
      });
      // 3) Cards da COZINHA vinculados (kdsQueue) — tombstone + hard-delete
      //    (antes ficavam órfãos na tela do KDS após excluir o pedido).
      try {
        const qItems = await db.kdsQueue
          .filter((q) => q.orderId === order.id || (order.mpPaymentId && q.mpPaymentId === order.mpPaymentId))
          .toArray();
        for (const q of qItems) {
          await addTombstone('kdsQueue', q.id, { by: operator?.name || null });
          await db.kdsQueue.delete(q.id); // hook 'deleting' → removeFromRemote
        }
      } catch (e) {
        console.warn('[ADMIN DELETE] limpeza kdsQueue falhou:', e?.message || e);
      }
      // 4) Hard-delete local (hook 'deleting' propaga removeFromRemote)
      await db.deliveryOrders.delete(order.id);
      // 5) Ponte backendOrders no Firebase + claim permanente (anti-ressurreição)
      if (order.mpPaymentId) {
        import('@/lib/firebaseSync')
          .then(({ purgeBackendOrderBridge }) => purgeBackendOrderBridge(order.mpPaymentId))
          .catch(() => {});
        // 6) Mongo (servidor) — exclusão definitiva
        import('@/lib/orderServerSync')
          .then(({ hardDeleteOrderOnServer }) => hardDeleteOrderOnServer(order))
          .catch(() => {});
      }
      toast.success(`Pedido #${order.id} excluído definitivamente`, {
        description: 'Removido do dispositivo, do Firebase, da cozinha e do servidor. Registrado no Log de Auditoria.',
      });
      setDeleteTarget(null);
      loadOrders();
    } catch (err) {
      toast.error(err?.message || 'Falha ao excluir pedido');
    }
  };

  const activeCount = orders.filter((o) => ['preparing', 'ready', 'delivering'].includes(o.status)).length;
  const readyOrders = orders.filter((o) => o.status === 'ready');

  // Group ready orders by neighborhood district
  const groupedByDistrict = {};
  readyOrders.forEach((o) => {
    const district = (o.neighborhood || 'Outros').split(' - ')[0];
    if (!groupedByDistrict[district]) groupedByDistrict[district] = [];
    groupedByDistrict[district].push(o);
  });
  const districtEntries = Object.entries(groupedByDistrict).sort((a, b) => b[1].length - a[1].length);

  return (
    <div data-testid="delivery-admin-page" className="p-6 lg:p-8">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h2 className="font-heading text-3xl tracking-tight font-bold text-zinc-900">Delivery</h2>
          <p className="text-sm text-zinc-500 mt-1">Gestao de pedidos delivery</p>
        </div>
        <div className="flex gap-2">
          <button data-testid="view-list-btn" onClick={() => setView('list')} className={`px-4 py-2 rounded-lg text-sm font-bold transition-all active:translate-y-[1px] min-h-[40px] ${view === 'list' ? 'bg-[#2563eb] text-white' : 'bg-white text-zinc-600 border-2 border-zinc-200 hover:border-zinc-400'}`}>
            Lista
          </button>
          <button data-testid="view-dispatch-btn" onClick={() => setView('dispatch')} className={`px-4 py-2 rounded-lg text-sm font-bold transition-all active:translate-y-[1px] min-h-[40px] flex items-center gap-1 ${view === 'dispatch' ? 'bg-[#2563eb] text-white' : 'bg-white text-zinc-600 border-2 border-zinc-200 hover:border-zinc-400'}`}>
            <Layers size={14} /> Despacho
            {readyOrders.length > 0 && <span className="bg-red-500 text-white text-xs font-bold w-5 h-5 rounded-full flex items-center justify-center ml-1">{readyOrders.length}</span>}
          </button>
        </div>
      </div>

      {/* DISPATCH VIEW - Orders grouped by neighborhood */}
      {view === 'dispatch' && (
        <div data-testid="dispatch-view">
          <p className="text-xs uppercase tracking-[0.2em] font-bold text-zinc-400 mb-4">
            Pedidos Prontos Agrupados por Bairro ({readyOrders.length} pedidos)
          </p>
          {districtEntries.length === 0 && (
            <div className="text-center py-16 text-zinc-300">
              <Package size={48} className="mx-auto mb-3 opacity-30" />
              <p className="font-semibold">Nenhum pedido pronto para despacho</p>
            </div>
          )}
          {districtEntries.map(([district, distOrders]) => (
            <div key={district} data-testid={`dispatch-group-${district}`} className="mb-6">
              <div className="flex items-center gap-2 mb-3">
                <MapPin size={16} className="text-[#2563eb]" />
                <span className="font-heading text-lg font-bold text-zinc-900">{district}</span>
                <Badge className="bg-blue-100 text-blue-700 border-blue-200">{distOrders.length} pedidos</Badge>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {distOrders.map((order) => (
                  <div key={order.id} data-testid={`dispatch-order-${order.id}`} className="bg-white border-2 border-blue-200 rounded-xl p-4">
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-heading font-bold text-zinc-900">#{order.id} - {order.customerName}</span>
                      <span className="font-mono text-sm font-bold text-zinc-900">{formatCurrency(order.total)}</span>
                    </div>
                    <p className="text-xs text-zinc-500 mb-1">{order.neighborhood} ({order.deliveryDistance}km)</p>
                    {order.address && <p className="text-xs text-zinc-500">{order.address}</p>}
                    {order.landmark && <p className="text-xs font-bold text-zinc-600">Ref: {order.landmark}</p>}
                    <div className="text-xs text-zinc-400 mt-1">
                      {order.items?.map((i) => `${i.quantity}x ${i.name}`).join(', ')}
                    </div>
                    <div className="flex gap-2 mt-3">
                      <button data-testid={`dispatch-send-${order.id}`} onClick={() => updateStatus(order.id, 'delivering')} className="flex-1 flex items-center justify-center gap-1 bg-purple-600 text-white rounded-lg font-bold py-2 text-sm active:translate-y-[1px] transition-all min-h-[40px]">
                        <Bike size={14} /> Enviar
                      </button>
                      <button onClick={() => showLabel(order, 'delivery')} className="w-10 h-10 rounded-lg border-2 border-zinc-200 flex items-center justify-center hover:border-zinc-400 transition-all" title="Etiqueta Entrega">
                        <Tag size={14} />
                      </button>
                      <button onClick={() => showLabel(order, 'kitchen')} className="w-10 h-10 rounded-lg border-2 border-zinc-200 flex items-center justify-center hover:border-zinc-400 transition-all" title="Etiqueta Cozinha">
                        <Printer size={14} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* LIST VIEW */}
      {view === 'list' && (
        <>
          <div className="flex gap-2 mb-6">
            {['active', 'delivered', 'all'].map((f) => (
              <button key={f} data-testid={`filter-${f}`} onClick={() => setFilter(f)} className={`px-4 py-2 rounded-lg text-sm font-bold transition-all active:translate-y-[1px] min-h-[40px] ${filter === f ? 'bg-[#2563eb] text-white' : 'bg-white text-zinc-600 border-2 border-zinc-200 hover:border-zinc-400'}`}>
                {f === 'active' ? `Ativos (${activeCount})` : f === 'delivered' ? 'Entregues' : 'Todos'}
              </button>
            ))}
          </div>

          <div className="space-y-4">
            {filtered.map((order) => {
              const statusInfo = STATUS_MAP[order.status] || STATUS_MAP.preparing;
              const StatusIcon = statusInfo.icon;
              return (
                <div key={order.id} data-testid={`delivery-order-${order.id}`} className="bg-white border-2 border-zinc-200 rounded-xl p-5">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <span className="font-heading text-lg font-bold text-zinc-900">#{order.id}</span>
                      <Badge className={statusInfo.color}><StatusIcon size={12} className="mr-1" />{statusInfo.label}</Badge>
                      {order.loyaltyDiscount > 0 && <Badge className="bg-amber-100 text-amber-700 border-amber-200 text-xs">Fidelidade</Badge>}
                    </div>
                    <span className="text-xs text-zinc-400 font-mono">{formatDateTime(order.createdAt)}</span>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <p className="text-xs uppercase tracking-wider font-bold text-zinc-400 mb-1">Cliente</p>
                      <p className="font-semibold text-zinc-900">{order.customerName}</p>
                      <p className="text-xs text-zinc-500">{order.customerPhone}</p>
                      <div className="flex items-center gap-1 text-xs text-zinc-400 mt-1"><MapPin size={12} />{order.neighborhood} ({order.deliveryDistance}km)</div>
                      {order.address && <p className="text-xs text-zinc-500 mt-0.5">{order.address}</p>}
                      {order.landmark && <p className="text-xs font-bold text-zinc-600">Ref: {order.landmark}</p>}
                    </div>
                    <div>
                      <p className="text-xs uppercase tracking-wider font-bold text-zinc-400 mb-1">Itens</p>
                      {order.items?.map((item, idx) => (
                        <div key={idx}>
                          <p className="text-sm text-zinc-700">{item.quantity}x {item.name}</p>
                          {item.notes && <p className="text-xs text-amber-600 font-semibold ml-3">Obs: {item.notes}</p>}
                        </div>
                      ))}
                    </div>
                    <div className="text-right">
                      <p className="text-xs uppercase tracking-wider font-bold text-zinc-400 mb-1">Total</p>
                      <p className="font-mono text-xl font-black text-zinc-900">{formatCurrency(order.total)}</p>
                      <p className="text-xs text-zinc-400">Entrega: {formatCurrency(order.deliveryFee)}</p>
                      {order.loyaltyDiscount > 0 && <p className="text-xs text-emerald-600 font-bold">Fidelidade: -{formatCurrency(order.loyaltyDiscount)}</p>}
                    </div>
                  </div>
                  <div className="flex gap-2 mt-4 pt-4 border-t border-zinc-100">
                    {order.status === 'preparing' && (
                      <button data-testid={`mark-ready-${order.id}`} onClick={() => updateStatus(order.id, 'ready')} className="flex-1 flex items-center justify-center gap-2 bg-blue-600 text-white rounded-lg font-bold py-3 min-h-[44px] active:translate-y-[1px] transition-all text-sm"><Package size={16} /> Pronto p/ Entrega</button>
                    )}
                    {order.status === 'ready' && (
                      <button data-testid={`mark-delivering-${order.id}`} onClick={() => updateStatus(order.id, 'delivering')} className="flex-1 flex items-center justify-center gap-2 bg-purple-600 text-white rounded-lg font-bold py-3 min-h-[44px] active:translate-y-[1px] transition-all text-sm"><Bike size={16} /> Saiu p/ Entrega</button>
                    )}
                    {order.status === 'delivering' && (
                      <button data-testid={`mark-delivered-${order.id}`} onClick={() => updateStatus(order.id, 'delivered')} className="flex-1 flex items-center justify-center gap-2 bg-emerald-600 text-white rounded-lg font-bold py-3 min-h-[44px] active:translate-y-[1px] transition-all text-sm"><ShieldCheck size={16} /> Entregue (PIN)</button>
                    )}
                    {order.status !== 'delivered' && (
                      <>
                        <button data-testid={`print-delivery-label-${order.id}`} onClick={() => showLabel(order, 'delivery')} className="flex items-center justify-center gap-1 bg-white text-zinc-700 rounded-lg font-bold border-2 border-zinc-300 px-3 py-3 min-h-[44px] hover:border-zinc-900 active:translate-y-[1px] transition-all text-sm"><Tag size={14} /> Entrega</button>
                        <button data-testid={`print-kitchen-label-${order.id}`} onClick={() => showLabel(order, 'kitchen')} className="flex items-center justify-center gap-1 bg-white text-zinc-700 rounded-lg font-bold border-2 border-zinc-300 px-3 py-3 min-h-[44px] hover:border-zinc-900 active:translate-y-[1px] transition-all text-sm"><Printer size={14} /> Cozinha</button>
                      </>
                    )}
                    <a href={`/rastreio/${order.id}`} target="_blank" rel="noreferrer" className="flex items-center justify-center gap-2 bg-white text-zinc-700 rounded-lg font-bold border-2 border-zinc-300 px-4 py-3 min-h-[44px] hover:border-zinc-900 active:translate-y-[1px] transition-all text-sm"><Eye size={16} /></a>
                    {isAdmin && (
                      <button
                        data-testid={`delivery-delete-${order.id}`}
                        onClick={() => setDeleteTarget(order)}
                        title="Excluir pedido (admin)"
                        className="flex items-center justify-center bg-white text-rose-500 rounded-lg font-bold border-2 border-rose-200 px-3 py-3 min-h-[44px] hover:border-rose-400 hover:bg-rose-50 active:translate-y-[1px] transition-all"
                      >
                        <Trash2 size={16} />
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
          {filtered.length === 0 && (
            <div className="text-center py-20 text-zinc-300">
              <Bike size={64} className="mx-auto mb-4 opacity-30" />
              <p className="text-lg font-semibold">Nenhum pedido {filter === 'active' ? 'ativo' : ''}</p>
            </div>
          )}
        </>
      )}

      {/* Label Preview Modal */}
      <Dialog open={!!labelPreview} onOpenChange={() => setLabelPreview(null)}>
        <DialogContent className="max-w-lg bg-white border-2 border-zinc-900 rounded-2xl shadow-[0_8px_32px_rgba(0,0,0,0.12)]">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl font-bold">
              {labelType === 'kitchen' ? 'Etiqueta Cozinha' : 'Etiqueta Entrega'}
            </DialogTitle>
            <DialogDescription>
              Preview dos comandos ESC/POS. Tags [BOLD], [2X], [UND] indicam formatacao.
            </DialogDescription>
          </DialogHeader>
          <pre data-testid="label-preview" className="bg-zinc-50 border border-zinc-200 rounded-lg p-4 font-mono text-xs whitespace-pre overflow-x-auto max-h-[400px] overflow-y-auto leading-relaxed">
            {labelPreview}
          </pre>
          <button onClick={() => { toast.success('Etiqueta enviada para impressao!'); setLabelPreview(null); }} className="w-full bg-[#2563eb] text-white rounded-lg font-bold py-3 mt-2 active:translate-y-[1px] transition-all min-h-[44px]">
            Imprimir
          </button>
        </DialogContent>
      </Dialog>

      {/* Fase 30 — Dialog: Excluir pedido de delivery (admin) */}
      <Dialog open={!!deleteTarget} onOpenChange={(o) => { if (!o) setDeleteTarget(null); }}>
        <DialogContent data-testid="delivery-delete-dialog" className="max-w-md bg-white border-2 border-rose-200 rounded-2xl">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl font-bold text-rose-700 flex items-center gap-2">
              <ShieldAlert size={20} /> Excluir Pedido #{deleteTarget?.id}?
            </DialogTitle>
            <DialogDescription>
              Esta ação <strong>não pode ser desfeita</strong> e ficará registrada no Log de Auditoria com seu nome.
            </DialogDescription>
          </DialogHeader>
          {deleteTarget && (
            <div className="bg-rose-50 border border-rose-200 rounded-xl p-4 space-y-1 text-sm">
              <p className="flex justify-between">
                <span className="text-slate-600">Cliente:</span>
                <strong>{deleteTarget.customerName || '—'}</strong>
              </p>
              <p className="flex justify-between">
                <span className="text-slate-600">Status:</span>
                <strong className="capitalize">{deleteTarget.status}</strong>
              </p>
              <p className="flex justify-between">
                <span className="text-slate-600">Total:</span>
                <strong className="font-mono text-rose-700">{formatCurrency(deleteTarget.total || 0)}</strong>
              </p>
              <p className="flex justify-between">
                <span className="text-slate-600">Bairro:</span>
                <strong>{deleteTarget.neighborhood || '—'}</strong>
              </p>
            </div>
          )}
          <DialogFooter className="gap-2 mt-2">
            <button
              data-testid="delivery-delete-cancel"
              onClick={() => setDeleteTarget(null)}
              className="flex-1 h-11 rounded-xl border-2 border-slate-200 bg-white text-slate-700 font-bold text-sm hover:bg-slate-50"
            >
              Cancelar
            </button>
            <button
              data-testid="delivery-delete-confirm"
              onClick={handleConfirmDelete}
              className="flex-1 h-11 rounded-xl bg-rose-600 text-white font-bold text-sm hover:bg-rose-700"
            >
              Excluir definitivamente
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* v7.33 — Diálogo de PIN para confirmar entrega */}
      <DeliveryPinDialog
        open={!!pinTarget}
        orderId={pinTarget?.id}
        customerName={pinTarget?.customerName}
        onClose={() => setPinTarget(null)}
        onConfirm={confirmDeliveryPin}
      />
    </div>
  );
}
